import React, { useContext, useState, useEffect, useRef } from "react";
import { makeStyles, Switch, FormControlLabel, Tooltip, withStyles } from "@material-ui/core";
import { CreatorModeContext } from "src/context/CreatorMode";

const POSITION_STORAGE_KEY = "creatorModeTogglePosition";

// ----------------------------
// iOS Style Switch (IMAGE LIKE)
// ----------------------------
const IOSSwitch = withStyles((theme) => ({
  root: {
    width: 42,
    height: 22,
    padding: 0,
    display: "flex",
    alignItems: "center",
  },
  switchBase: {
    padding: 0,
    "&$checked": {
      transform: "translateX(20px)",
      color: "#fff",
      "& + $track": {
        backgroundColor: " #FF7F26",
        opacity: 1,
        border: "none",
      },
    },
    "&$focusVisible $thumb": {
      color: " #FF7F26",
      border: "6px solid #fff",
    },
  },
  thumb: {
    width: 22,
    height: 22,
    backgroundColor: "#fff",
    boxShadow: "0px 2px 4px rgba(0,0,0,0.2)",
  },
  track: {
    borderRadius: 24  ,
    backgroundColor: " #D9D9D9",
    opacity: 1,
    transition: theme.transitions.create(["background-color", "border"]),
    border: "none",
  },
  checked: {},
  focusVisible: {},
}))(Switch);

// ----------------------------
// MAIN COMPONENT
// ----------------------------
const useStyles = makeStyles((theme) => ({
  toggleContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    position: (props) => (props.isCreatorMode && !props.inline ? "fixed" : "relative"),
    zIndex: (props) => (props.isCreatorMode && !props.inline ? 10001 : "auto"),
    cursor: (props) => (props.isCreatorMode && !props.inline ? "move" : "default"),
    userSelect: "none",
    transition: (props) => (props.isDragging ? "none" : "all 0.2s ease"),
    backgroundColor: (props) =>
      props.isCreatorMode && !props.inline ? "rgba(30, 30, 30, 0.95)" : "transparent",
    padding: (props) => (props.isCreatorMode && !props.inline ? "10px 16px" : "0"),
    borderRadius: (props) => (props.isCreatorMode && !props.inline ? "8px" : "0"),
    boxShadow: (props) =>
      props.isCreatorMode && !props.inline
        ? "0 4px 12px rgba(0, 0, 0, 0.5)"
        : "none",
    backdropFilter: (props) => (props.isCreatorMode && !props.inline ? "blur(10px)" : "none"),
    minWidth: (props) => (props.isCreatorMode && !props.inline ? "180px" : "auto"),
    minHeight: (props) => (props.isCreatorMode && !props.inline ? "40px" : "auto"),
  },
  toggleLabel: {
    margin: 0,
    color: (props) => (props.isCreatorMode ? "#ffffff" : "#262626"),
    fontSize: "14px",
    fontWeight: 500,
    fontFamily: "'Poppins', sans-serif",
    display: "flex",
    alignItems: "center",
    pointerEvents: (props) => (props.isDragging ? "none" : "auto"),
    "& .MuiFormControlLabel-label": {
      marginLeft: theme.spacing(1),
      lineHeight: "1.5",
      textTransform: (props) => (props.inline ? "uppercase" : "none"),
      fontSize: (props) => (props.inline ? "16px" : "14px"),
      fontWeight: (props) => (props.inline ? 400 : 500),
    },
  },
}));

const CreatorModeToggle = ({ className, style, inline = false }) => {
  const { isCreatorMode, toggleCreatorMode } = useContext(CreatorModeContext);
  const [position, setPosition] = useState({ x: 20, y: 20 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const containerRef = useRef(null);

  useEffect(() => {
    const savedPosition = localStorage.getItem(POSITION_STORAGE_KEY);
    if (savedPosition) {
      try {
        const parsed = JSON.parse(savedPosition);
        setPosition({ x: parsed.x || 24, y: parsed.y || 24 });
      } catch {
        setPosition({ x: 24, y: 24 });
      }
    }
  }, []);

  // Save position to localStorage
  useEffect(() => {
    if (isCreatorMode && position) {
      localStorage.setItem(POSITION_STORAGE_KEY, JSON.stringify(position));
    }
  }, [position, isCreatorMode]);

  const handleMouseDown = (e) => {
    if (!isCreatorMode || inline) return;

    const target = e.target;
    const notDraggable =
      target.closest(".MuiSwitch-root") ||
      target.closest("input") ||
      target.closest(".MuiFormControlLabel-root");

    if (notDraggable) return;

    setIsDragging(true);
    const rect = containerRef.current?.getBoundingClientRect();
    if (rect) {
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      });
    }

    e.preventDefault();
    e.stopPropagation();
  };

  const handleMouseMove = (e) => {
    if (!isDragging || !isCreatorMode) return;

    const newX = e.clientX - dragOffset.x;
    const newY = e.clientY - dragOffset.y;

    const maxX = window.innerWidth - (containerRef.current?.offsetWidth || 200);
    const maxY = window.innerHeight - (containerRef.current?.offsetHeight || 50);

    setPosition({
      x: Math.max(0, Math.min(newX, maxX)),
      y: Math.max(0, Math.min(newY, maxY)),
    });
  };

  const handleMouseUp = () => setIsDragging(false);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "move";
      document.body.style.userSelect = "none";

      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
      };
    }
  }, [isDragging]);

  const classes = useStyles({ isCreatorMode, isDragging, inline });

  const containerStyle = isCreatorMode && !inline
    ? {
        left: `${position.x}px`,
        top: `${position.y}px`,
        right: "auto",
        bottom: "auto",
        ...style,
      }
    : style;

  return (
    <div
      ref={containerRef}
      className={`${classes.toggleContainer} ${className || ""}`}
      style={containerStyle}
      onMouseDown={handleMouseDown}
    >
      <Tooltip title={isCreatorMode ? "Drag background to move | Click switch to toggle" : "Enable Creator Mode"}>
        <FormControlLabel
          control={
            <IOSSwitch
              checked={isCreatorMode}
              onChange={(e) => {
                e.stopPropagation();
                toggleCreatorMode();
              }}
              onClick={(e) => {
                e.stopPropagation();
              }}
              onMouseDown={(e) => {
                e.stopPropagation();
              }}
              style={{
                pointerEvents: isDragging ? "none" : "auto",
                marginRight: "8px",
              }}
            />
          }
          label={isCreatorMode ? "Creator Mode ON" : "Creator Mode OFF"}
          className={classes.toggleLabel}
          style={{
            pointerEvents: isDragging ? "none" : "auto",
            alignItems: "center",
          }}
          labelPlacement="end"
        />
      </Tooltip>
    </div>
  );
};

export default CreatorModeToggle;
